import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  forwardRef,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** On/off switch for settings, with the same API as app-checkbox. */
@Component({
  selector: 'app-switch',
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
})
export class SwitchComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly disabled = input(false);
  readonly value = input<boolean>(false);
  /** `sm` draws the smaller track, for switches inside dense list rows. */
  readonly size = input<'sm' | 'md'>('md');
  /** Accessible name when there is no `label` (the input sits in a shadow root). */
  readonly ariaLabel = input<string | null>(null);

  private readonly disabledByCva = signal(false);
  readonly isDisabled = computed(() => this.disabled() || this.disabledByCva());

  /** Follows the `value` input; writeValue() and user toggles set it directly. */
  readonly checked = linkedSignal<boolean, boolean>({
    source: this.value,
    computation: (src) => src || false,
  });

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: boolean): void {
    this.checked.set(value || false);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  onInputChange(event: Event): void {
    const next = (event.target as HTMLInputElement).checked;
    if (next === this.checked()) return;
    this.checked.set(next);
    this.onChange(next);
  }

  onBlur(): void {
    this.onTouched();
  }
}
